import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["checkboxes", "pills", "dropdown", "search", "options"]

  connect() {
    this._outsideClick = this._outsideClick.bind(this)
    this._buildOptions()
    this._syncPills()
  }

  disconnect() {
    document.removeEventListener("click", this._outsideClick)
  }

  toggle(event) {
    event.stopPropagation()
    this._isOpen ? this.close() : this._open()
  }

  close() {
    this.dropdownTarget.classList.add("hidden")
    document.removeEventListener("click", this._outsideClick)
    if (this.hasSearchTarget) {
      this.searchTarget.value = ""
      this._applyFilter("")
    }
  }

  filter() {
    this._applyFilter(this.hasSearchTarget ? this.searchTarget.value : "")
  }

  selectOption(event) {
    event.stopPropagation()
    const value = event.currentTarget.dataset.msValue
    const cb = this._findCheckbox(value)
    if (!cb) return
    cb.checked = !cb.checked
    cb.dispatchEvent(new Event("change", { bubbles: true }))
    this._updateOptionRow(event.currentTarget, cb.checked)
    this._syncPills()
  }

  removePill(event) {
    event.stopPropagation()
    const value = event.currentTarget.dataset.msValue
    const cb = this._findCheckbox(value)
    if (cb) {
      cb.checked = false
      cb.dispatchEvent(new Event("change", { bubbles: true }))
      const rows = Array.from(this.optionsTarget.querySelectorAll("[data-ms-value]"))
      const matchRow = rows.find(r => r.dataset.msValue === value)
      if (matchRow) this._updateOptionRow(matchRow, false)
    }
    this._syncPills()
  }

  // ── private ────────────────────────────────────────────────────────────────

  get _isOpen() {
    return !this.dropdownTarget.classList.contains("hidden")
  }

  _open() {
    this.dropdownTarget.classList.remove("hidden")
    if (this.hasSearchTarget) this.searchTarget.focus()
    document.addEventListener("click", this._outsideClick)
  }

  _outsideClick(event) {
    if (!this.element.contains(event.target)) this.close()
  }

  _findCheckbox(value) {
    return Array.from(this.checkboxesTarget.querySelectorAll("input[type=checkbox]"))
      .find(el => el.value === value) || null
  }

  _buildOptions() {
    const checkboxes = Array.from(this.checkboxesTarget.querySelectorAll("input[type=checkbox]"))
    this.optionsTarget.innerHTML = ""
    checkboxes.forEach(cb => {
      const label = cb.dataset.msLabel || cb.value
      const row = document.createElement("div")
      row.dataset.msValue = cb.value
      row.dataset.action = "click->admin-multiselect#selectOption"
      row.className = [
        "flex items-center gap-2.5 px-3 py-2 cursor-pointer text-sm rounded-md transition-colors",
        cb.checked ? "bg-[#084572]/8" : "hover:bg-gray-50"
      ].join(" ")
      row.innerHTML = this._indicatorHTML(cb.checked) + `<span class="text-gray-700 leading-snug">${label}</span>`
      this.optionsTarget.appendChild(row)
    })
  }

  _syncPills() {
    const checked = Array.from(this.checkboxesTarget.querySelectorAll("input:checked"))
    this.pillsTarget.innerHTML = ""

    if (checked.length === 0) {
      const placeholder = document.createElement("span")
      placeholder.className = "text-sm text-gray-400 select-none pointer-events-none"
      placeholder.textContent = this.element.dataset.msPlaceholder || "Select..."
      this.pillsTarget.appendChild(placeholder)
    } else {
      checked.forEach(cb => {
        const label = cb.dataset.msLabel || cb.value
        const pill = document.createElement("span")
        pill.className = "inline-flex items-center gap-1 pl-2 pr-1 py-0.5 rounded-full text-xs font-medium bg-[#084572]/10 text-[#084572]"
        pill.innerHTML =
          `<span>${label}</span>` +
          `<button type="button" data-ms-value="${cb.value}" data-action="click->admin-multiselect#removePill" ` +
          `class="flex items-center justify-center w-3.5 h-3.5 rounded-full hover:bg-[#084572]/20 text-[#084572]/50 hover:text-[#084572] transition-colors">` +
          `<svg viewBox="0 0 20 20" fill="currentColor" class="w-2.5 h-2.5">` +
          `<path d="M6.28 5.22a.75.75 0 00-1.06 1.06L8.94 10l-3.72 3.72a.75.75 0 101.06 1.06L10 11.06l3.72 3.72a.75.75 0 101.06-1.06L11.06 10l3.72-3.72a.75.75 0 00-1.06-1.06L10 8.94 6.28 5.22z"/>` +
          `</svg></button>`
        this.pillsTarget.appendChild(pill)
      })
    }
  }

  _applyFilter(query) {
    const q = query.toLowerCase().trim()
    Array.from(this.optionsTarget.querySelectorAll("[data-ms-value]")).forEach(row => {
      const text = row.querySelector("span")?.textContent.toLowerCase() || ""
      row.classList.toggle("hidden", q.length > 0 && !text.includes(q))
    })
  }

  _updateOptionRow(row, checked) {
    row.classList.toggle("bg-[#084572]/8", checked)
    row.classList.toggle("hover:bg-gray-50", !checked)
    const indicator = row.querySelector("[data-ms-indicator]")
    if (indicator) indicator.outerHTML = this._indicatorHTML(checked)
  }

  _indicatorHTML(checked) {
    return checked
      ? `<div data-ms-indicator class="h-4 w-4 rounded border-2 bg-[#084572] border-[#084572] shrink-0 flex items-center justify-center">` +
        `<svg class="h-2.5 w-2.5 text-white" viewBox="0 0 20 20" fill="currentColor">` +
        `<path fill-rule="evenodd" d="M16.704 4.153a.75.75 0 01.143 1.052l-8 10.5a.75.75 0 01-1.127.075l-4.5-4.5a.75.75 0 011.06-1.06l3.894 3.893 7.48-9.817a.75.75 0 011.05-.143z" clip-rule="evenodd"/>` +
        `</svg></div>`
      : `<div data-ms-indicator class="h-4 w-4 rounded border-2 border-gray-300 shrink-0 bg-white"></div>`
  }
}
